import { ComponentProps, memo, useCallback } from 'react'
import { ListItemAvatar, ListItemButton, ListItemText } from '@mui/material'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { useChat, useContactDescription } from 'hooks/chat'
import { Contact_Contact$key } from '__generated__/Contact_Contact.graphql'
import RecipientAvatar from 'components/RecipientAvatar'

interface IContactProps extends ComponentProps<typeof ListItemButton> {
  contactFragmentRef: Contact_Contact$key
  hideRelationship?: boolean
  hideOnlineStatus?: boolean
}

function Contact({
  contactFragmentRef,
  onClick,
  hideRelationship,
  hideOnlineStatus,
  children,
  ...otherProps
}: IContactProps) {
  const { openChat } = useChat()
  const contact = useFragment(graphql`
    fragment Contact_Contact on Recipient {
      ... on User {
        id
        name

        directChatThread {
          id
        }
      }

      ... on ChatBot {
        id
        name

        directChatThread {
          id
        }
      }

      ...RecipientAvatar_Recipient
      ...chat_useContactDescription
    }
  `, contactFragmentRef)

  const openDirectChat = useCallback(() => (
    openChat(contact?.directChatThread?.id ? {
      id: contact.directChatThread.id,
    } : {
      recipientId: contact.id,
    })
  ), [contact])

  const description = useContactDescription(contact, {
    hideRelationship,
    hideOnlineStatus,
  })

  if (!contact) return null

  return (
    <ListItemButton onClick={onClick || openDirectChat} {...otherProps}>
      <ListItemAvatar>
        <RecipientAvatar
          recipientFragmentRef={contact}
        />
      </ListItemAvatar>

      <ListItemText
        primary={contact.name || 'Unnamed User'}
        secondary={description}
      />

      {children}
    </ListItemButton>
  )
}

export default memo(Contact)
